import { CSSProperties, FC, Suspense, useMemo } from "react";
import { Outlet, useOutletContext, useParams } from "react-router-dom";
import { Navbar } from "@components/navbar";
import { Loader } from "@components/loader";
import { AppMenuNavigate } from "@components/app-menu";
import { AWSUser } from "@archetypes/auth";
import ErrorPageLayout from "@components/error-page-layout";
import Error404Image from "@assets/404.svg";
import { usePublishedApp } from "@contexts/live-app.context";
import { useULAppTheme } from "@hooks/use-ul-app-theme";
import { PublishedAppRouteGate } from "./published-app-route-gate";

const PublishedAppLayout: FC = () => {
  const user = useOutletContext<AWSUser>();
  const {
    appData,
    appError,
    menuTree,
    showLeftMenu,
    onHamburgerClick,
    onNavbarButtonClick,
    isAppDataLoading,
  } = usePublishedApp();
  const { appId } = useParams();

  const context = useMemo(() => {
    return {
      user,
    };
  }, [user]);

  // Per-app theme exposed as CSS variables on the shell so descendants can paint
  // immediately (falling back to the brand defaults) instead of the whole layout
  // blocking on GET_APP. When app data resolves, these update and repaint without
  // remounting the route or its in-flight queries.
  const themeStyle = useMemo(
    () =>
      ({
        "--ul-app-primary": appData?.getApp?.primaryColor,
        "--ul-app-font": appData?.getApp?.fontColor,
      }) as CSSProperties,
    [appData],
  );

  useULAppTheme({
    "--ul-app-primary": appData?.getApp?.primaryColor,
    "--ul-app-font": appData?.getApp?.fontColor,
  });

  if (isAppDataLoading) {
    return <Loader variant="full-screen-static" className="w-100 h-100vh-50px" />;
  }

  return (
    <main className="main-container" style={themeStyle}>
      <div className="content-area w-100">
        <header className="header-area">
          <Navbar
            basePath="/apps"
            user={user}
            appData={appData?.getApp}
            navbarClassName={showLeftMenu ? "open" : ""}
            onHamburgerClick={onHamburgerClick}
            onNavbarButtonClick={onNavbarButtonClick}
            primaryColor={appData?.getApp?.primaryColor}
          />
        </header>

        <div className="flex">
          {appError ? (
            <ErrorPageLayout
              title={
                (appError as Error & { cause?: { message?: string } }).cause?.message ??
                "Error Fetching App Details"
              }
              image={<img src={Error404Image} alt="error" decoding="async" loading="eager" />}
            />
          ) : (
            <>
              <AppMenuNavigate
                menuData={menuTree}
                appId={appId as string}
                primaryColor={appData?.getApp?.primaryColor}
                fontColor={appData?.getApp?.fontColor}
              />
              <div className="app-content-area">
                <PublishedAppRouteGate>
                  {/* Scope the lazy-page chunk-load fallback to exactly the routed
                      page, inside the content area, so it renders beside the left
                      menu instead of escaping to the top-level full-screen Suspense
                      in main.tsx. The gate's own loader covers route validation. */}
                  <Suspense
                    fallback={
                      <Loader variant="full-screen-static" className="w-100 h-100vh-50px" />
                    }
                  >
                    <Outlet context={context} />
                  </Suspense>
                </PublishedAppRouteGate>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default PublishedAppLayout;
